import type {
    LibraryHealthResponse,
} from "@mrflark/spl_sdk";
import {
    Skeleton,
} from "@mrflark/ui";
import {
    useQuery,
} from "@tanstack/react-query";
import {
    AlertCircle,
    Camera,
    Clock,
    Eye,
    Film,
    Hash,
    HeartPulse,
    Loader2,
    Tags,
    Zap,
} from "lucide-preact";
import {
    splClient,
} from "../../api/client.ts";

const StatCard = ({
    label,
    value,
    icon: Icon,
    color = "text-text-primary",
}: {
    label: string;
    value: string | number;
    icon: typeof HeartPulse;
    color?: string;
}) => {
    return (
        <div className="bg-bg-secondary rounded-lg border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className="text-text-secondary" />
                <span className="text-xs text-text-secondary uppercase tracking-wide">
                    {label}
                </span>
            </div>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
        </div>
    );
};

const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
};

const formatConfidence = (value: number): string => {
    return value.toFixed(3);
};

const TagBar = ({
    tag,
    count,
    maxCount,
}: {
    tag: string;
    count: number;
    maxCount: number;
}) => {
    const widthPercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
    return (
        <div className="flex items-center gap-3">
            <span
                className="text-sm text-text-secondary w-32 truncate"
                title={tag}
            >
                {tag}
            </span>
            <div className="flex-1 h-5 bg-bg-tertiary rounded overflow-hidden">
                <div
                    className="h-full bg-accent/60 rounded"
                    style={{
                        width: `${widthPercent}%`,
                    }}
                />
            </div>
            <span className="text-xs text-text-secondary w-12 text-right">
                {count}
            </span>
        </div>
    );
};

export const HealthDashboard = () => {
    const {
        data: health,
        isLoading,
        error,
    } = useQuery<LibraryHealthResponse>({
        queryKey: ["libraryHealth"],
        queryFn: () => splClient.getLibraryHealth(),
        refetchInterval: 60000,
    });

    if (isLoading) {
        return (
            <div className="max-w-6xl mx-auto p-4 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.from({
                        length: 8,
                    }).map((_, index) => (
                        <Skeleton key={index} className="h-24 rounded-lg" />
                    ))}
                </div>
                <Skeleton className="h-64 rounded-lg" />
            </div>
        );
    }

    if (error || !health) {
        return (
            <div className="max-w-6xl mx-auto p-4">
                <div className="bg-bg-secondary rounded-lg border border-red-500/30 p-6 text-center">
                    <AlertCircle
                        size={24}
                        className="mx-auto mb-2 text-red-400"
                    />
                    <p className="text-text-secondary">
                        Failed to load health data
                    </p>
                </div>
            </div>
        );
    }

    const maxTagCount = health.top_tags.length > 0
        ? health.top_tags[0].count
        : 0;

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                    label="Total Items"
                    value={health.total_items.toLocaleString()}
                    icon={Hash}
                />
                <StatCard
                    label="Untagged"
                    value={health.untagged_count.toLocaleString()}
                    icon={Tags}
                    color={health.untagged_count > 0
                        ? "text-yellow-400"
                        : "text-text-primary"}
                />
                <StatCard
                    label="Never Viewed"
                    value={health.never_viewed_count.toLocaleString()}
                    icon={Eye}
                />
                <StatCard
                    label="Review Queue"
                    value={health.review_queue_count.toLocaleString()}
                    icon={Loader2}
                    color={health.review_queue_count > 0
                        ? "text-yellow-400"
                        : "text-text-primary"}
                />
                <StatCard
                    label="Failed Processing"
                    value={health.failed_processing_count.toLocaleString()}
                    icon={AlertCircle}
                    color={health.failed_processing_count > 0
                        ? "text-red-400"
                        : "text-text-primary"}
                />
                <StatCard
                    label="AI Coverage"
                    value={formatPercent(health.ai_coverage_percent)}
                    icon={Zap}
                    color={health.ai_coverage_percent < 80
                        ? "text-yellow-400"
                        : "text-green-400"}
                />
                <StatCard
                    label="Avg Confidence"
                    value={formatConfidence(health.avg_confidence)}
                    icon={HeartPulse}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-bg-secondary rounded-lg border border-border p-4">
                    <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Clock size={16} />
                        Recent Activity
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-secondary">
                                Added (24h)
                            </span>
                            <span className="text-lg font-bold text-text-primary">
                                {health.items_added_24h.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-secondary">
                                Added (7d)
                            </span>
                            <span className="text-lg font-bold text-text-primary">
                                {health.items_added_7d.toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-bg-secondary rounded-lg border border-border p-4">
                    <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Camera size={16} />
                        Media Types
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-secondary flex items-center gap-2">
                                <Camera size={14} /> Images
                            </span>
                            <span className="text-lg font-bold text-text-primary">
                                {health.media_type_breakdown.image
                                    .toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-secondary flex items-center gap-2">
                                <Film size={14} /> Videos
                            </span>
                            <span className="text-lg font-bold text-text-primary">
                                {health.media_type_breakdown.video
                                    .toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {health.top_tags.length > 0 && (
                <div className="bg-bg-secondary rounded-lg border border-border p-4">
                    <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Tags size={16} />
                        Top Tags
                    </h3>
                    <div className="space-y-2">
                        {health.top_tags.map((entry) => (
                            <TagBar
                                key={entry.tag}
                                tag={entry.tag}
                                count={entry.count}
                                maxCount={maxTagCount}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
